<script setup>
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Color from '@tiptap/extension-color'
import { TextStyle } from '@tiptap/extension-text-style'
import Highlight from '@tiptap/extension-highlight'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import ArtifactCardNode from '@/components/wysiwyg-components/nodes/artifact/card/ArtifactCardNode.js'
import InlineArtifactNode from '@/components/wysiwyg-components/nodes/artifact/inline/InlineArtifactNode.js'
import InlineIncidentNode from '@/components/wysiwyg-components/nodes/incident/inline/InlineIncidentNode.js'
import IncidentCardNode from '@/components/wysiwyg-components/nodes/incident/card/IncidentCardNode.js'
import CanvasNode from '@/components/wysiwyg-components/nodes/canvas/block/CanvasNode.js'
import InlineCanvasChip from '@/components/wysiwyg-components/nodes/canvas/inline/InlineCanvasChip.js'
import ChartBlockNode from '@/components/wysiwyg-components/nodes/chart/block/ChartBlockNode.js'
import InlineChartNode from '@/components/wysiwyg-components/nodes/chart/inline/InlineChartNode.js'
import { provide } from 'vue'
import CustomNodeMenu from '@/components/wysiwyg-components/editor/CustomNodeMenu.vue'
import TextFormattingMenus from '@/components/wysiwyg-components/editor/TextFormattingMenus.vue'
import Placeholder from '@tiptap/extension-placeholder'

const props = defineProps({
  readOnly: { type: Boolean, default: false },
  label: { type: String, default: null, required: false },
  rules: { type: Array, default: () => [] },
  hideDetails: { type: Boolean, default: false },
  placeholder: { type: String, default: null },
})

const model = defineModel({ type: String, default: '' })

const input = ref(null)
const isFocused = ref(false)

provide('isEditorFocused', isFocused)

defineExpose({
  validate: input.value?.validate,
  reset: input.value?.reset,
  errorMessages: input.value?.errorMessages,
  resetValidation: input.value?.resetValidation,
  isValid: input.value?.isValid,
})

const editor = useEditor({
  onFocus: () => {
    isFocused.value = true
  },
  onBlur: () => {
    isFocused.value = false
  },
  editable: props.readOnly !== true,
  content: model.value,

  extensions: [
    StarterKit.configure({
      history: true,
    }),
    Color,
    TextStyle,
    Highlight,
    InlineIncidentNode,
    IncidentCardNode,
    InlineArtifactNode,
    ArtifactCardNode,
    CanvasNode,
    InlineCanvasChip,
    ChartBlockNode,
    InlineChartNode,
    Placeholder.configure({ placeholder: props.placeholder ?? '' }),
  ],
})

const minHeight = computed(() => {
  if (props.readOnly === true) return 0
  return '100px'
})

watch(
  () => model.value,
  value => {
    const hasChanged = editor.value.getHTML() !== value
    if (hasChanged) {
      editor.value.commands.setContent(value, false)
    }
  },
  { deep: true }
)

watch(
  () => editor.value?.getHTML(),
  value => {
    if (props.readOnly === true) return
    const hasChanged = value !== model.value
    if (hasChanged) {
      model.value = value
    }
  },
  { deep: true }
)

onBeforeUnmount(() => {
  editor.value.destroy()
})
</script>

<template>
  <v-input
    ref="input"
    :validation-value="model"
    :rules="rules"
    :hide-details="props.hideDetails"
  >
    <template #default>
      <v-container class="ma-0 pa-0">
        <v-label
          v-if="props.label"
          :text="props.label"
        />
        <v-container
          v-if="editor && editor.isEditable"
          :fluid="true"
          class="ma-0 pa-0"
        >
          <div class="d-flex align-center justify-center flex-wrap">
            <TextFormattingMenus :editor="editor" />

            <v-btn
              icon="mdi-format-indent-decrease"
              variant="text"
              :disabled="!editor.can().liftListItem('listItem')"
              class="rounded-0"
              @click="editor.chain().focus().liftListItem('listItem').run()"
            />

            <CustomNodeMenu :editor="editor" />
          </div>
        </v-container>
        <editor-content
          :editor="editor"
          :class="{ 'editable border': !readOnly }"
        />
      </v-container>
    </template>
  </v-input>
</template>

<style>
.ProseMirror {
  min-height: v-bind(minHeight);
  padding: 10px;
}

.editable .ProseMirror {
  padding-bottom: 36px;
}
.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
  pointer-events: none;
  height: 0;
}
</style>
